import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const textClass = scrolled
    ? 'text-foreground-950'
    : 'text-background-50';
  const navBgClass = scrolled
    ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70'
    : 'bg-transparent';
  const mutedTextClass = scrolled
    ? 'text-foreground-700 hover:text-foreground-950'
    : 'text-background-50/85 hover:text-background-50';
  const dropdownBgClass = scrolled
    ? 'bg-background-50 border-background-200 text-foreground-700'
    : 'bg-foreground-950/90 backdrop-blur-sm border-background-50/20 text-background-50/85';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className={`flex items-center gap-2 ${scrolled ? '' : ''}`}>
            <span className={`text-lg md:text-xl font-heading font-bold italic tracking-tight ${textClass}`}>
              OOPBuy <span className={scrolled ? 'text-primary-500' : 'text-accent-400'}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${mutedTextClass}`}>
              Home
            </Link>
            <button onClick={() => scrollToSection('about')} className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${mutedTextClass}`}>
              Guide
            </button>
            <Link to="/article/oopbuy-spreadsheet-complete-guide" className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${mutedTextClass}`}>
              Shopping Guide
            </Link>
            <button onClick={() => scrollToSection('blog')} className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${mutedTextClass}`}>
              Blog
            </button>
            <button onClick={() => scrollToSection('safety')} className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${mutedTextClass}`}>
              Safety
            </button>
            <button onClick={() => scrollToSection('faq')} className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${mutedTextClass}`}>
              FAQ
            </button>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm transition-all cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400'
                    : 'border-background-50/30 text-background-50 hover:border-background-50/60 hover:bg-background-50/10'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className={`absolute top-full mt-2 right-0 w-72 rounded-xl py-2 shadow-sm max-h-96 overflow-y-auto z-50 border ${dropdownBgClass}`}>
                  <p className="px-4 py-1.5 text-xs font-medium opacity-60 uppercase tracking-wider">Browse 14 Categories</p>
                  {categories.map((cat, idx) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        scrolled
                          ? 'text-foreground-700 hover:text-accent-600 hover:bg-accent-50'
                          : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                      }`}
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${scrolled ? 'bg-accent-400' : 'bg-accent-400'}`}></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50 text-foreground-950 hover:bg-background-100'
              }`}
            >
              Visit Main Platform
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${textClass}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-foreground-950/95 backdrop-blur-sm border-t border-background-50/10 px-4 py-5 space-y-1">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-background-50/90 hover:bg-background-50/10 cursor-pointer whitespace-nowrap">
            <i className="ri-home-4-line text-lg"></i>
            Home
          </Link>
          <button onClick={() => scrollToSection('about')} className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-lg text-sm text-background-50/90 hover:bg-background-50/10 cursor-pointer whitespace-nowrap">
            <i className="ri-book-open-line text-lg"></i>
            Guide
          </button>
          <Link to="/article/oopbuy-spreadsheet-complete-guide" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-background-50/90 hover:bg-background-50/10 cursor-pointer whitespace-nowrap">
            <i className="ri-shopping-bag-3-line text-lg"></i>
            Shopping Guide
          </Link>
          <button onClick={() => scrollToSection('blog')} className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-lg text-sm text-background-50/90 hover:bg-background-50/10 cursor-pointer whitespace-nowrap">
            <i className="ri-article-line text-lg"></i>
            Blog
          </button>
          <button onClick={() => scrollToSection('safety')} className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-lg text-sm text-background-50/90 hover:bg-background-50/10 cursor-pointer whitespace-nowrap">
            <i className="ri-shield-check-line text-lg"></i>
            Safety
          </button>
          <button onClick={() => scrollToSection('faq')} className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-lg text-sm text-background-50/90 hover:bg-background-50/10 cursor-pointer whitespace-nowrap">
            <i className="ri-question-answer-line text-lg"></i>
            FAQ
          </button>

          <div className="pt-3 mt-3 border-t border-background-50/10">
            <p className="px-3 py-2 text-xs text-background-50/50 font-medium uppercase tracking-wider">Categories</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-background-50/80 hover:bg-background-50/10 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="pt-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              Visit Main Platform
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}